import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Organization } from '../model/organization';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { OrganizationList } from 'src/app/organization/model/organizationList';
import { Subject } from 'rxjs';
import { OrganizationEdit } from 'src/app/organization/model/organizationEdit';
import { Department } from '../model/department';

const organizationsCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class OrganizationService
{
  constructor(private http: HttpClient) { }

  /**
   * Save a organization.
   *
   * @param id Organization id
   * @param data Organization to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: organizationsCacheBuster
    })
  public saveOrganization(data: any, isNew: boolean, organizationId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/organizations', data);
    }
    else
    {
      return this.http.put(`/organizations/${organizationId}`, data);
    }
  }

  /**
   * Get all cached Organizations
   */
  @Cacheable(
    {
      cacheBusterObserver: organizationsCacheBuster
    })
  public getCachedOrganizations(): Observable<Organization[]>
  {
    return this.http.get<Organization[]>(`/organizations`);
  }

  /**
   * Delete a Organization.
   *
   * @param id Organization id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: organizationsCacheBuster
    })
  public deleteOrganization(id: number): Observable<OrganizationList>
  {
    return this.http.delete<OrganizationList>(`/organizations/${id}`);
  }

  /**
   * Get all cached Departments
   */
  @CacheBuster(
    {
      cacheBusterNotifier: organizationsCacheBuster
    })
  public getCachedDepartments(): Observable<Department[]>
  {
    return this.http.get<Department[]>(`/departments`);
  }

  /**
   * Check if department is in use
   *
   * @param data Department Id
   */
  public existsDepartment(id: number): Observable<any>
  {
    return this.http.get(`/departments/inuse/${id}`);
  }

  /**
   * Get Organizations for grid list.
   */
  public getOrganizationList(): Observable<OrganizationList[]>
  {
    return this.http.get<OrganizationList[]>('/organizations');
  }

  public getBillingOrganizations(id: number): Observable<any>
  {
    return this.http.get(`/organizations/${id}/billing/organizations`);
  }

  /**
   * Check if organization is in use
   *
   * @param data Organization Id
   */
  public existsOrganization(id: number): Observable<any>
  {
    return this.http.get(`/organizations/inuse/${id}`);
  }

  /**
   * Get organization by id.
   *
   * @param id Organization id
   */
  public getOrganizationById(id: number): Observable<OrganizationEdit>
  {
    return this.http.get<OrganizationEdit>(`/organizations/${id}`);
  }

  /**
   * Check if Organization Name exists to avoid name duplicates
   *
   * @param name Organization Name
   * @param id Organization Id
   */
  public existsOrganizationName(name: string): Observable<any>
  {
    return this.http.get(`/organizations/exists?name=${name}`);
  }

  /**
   * Check if Extension exists for organization
   *
   * @param key Extension key
   * @param organizationId Organization Id
   * @param extension Extension object
   */
  public existsSettingExtension(key: string, organizationId: number, category: string): Observable<any>
  {
    return this.http.get(`/setting/extension/exists?key=${key}&organizationId=${organizationId}&category=${category}`);
  }
}
